import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { doc, getDoc } from 'firebase/firestore';
import { db } from '../firebase';
import { PLAYBOOK_TYPES } from '../types/picks';
import { formatReturnsCentered, returnsToCentered } from '../utils/returns';

const PLAYBOOK_ID = `odikai_${PLAYBOOK_TYPES.GROWTH}`;
const CHART_WIDTH = 400;
const CHART_HEIGHT = 120;

// Running total of (return - 1) per event, as SVG points
function cumulativePath(returnsByEvent) {
  const totals = [];
  let total = 0;
  returnsByEvent.forEach((r) => {
    if (typeof r === 'number' && !Number.isNaN(r)) {
      total += r - 1;
      totals.push(total);
    }
  });
  if (totals.length < 2) return null;

  const min = Math.min(0, ...totals);
  const max = Math.max(0, ...totals);
  const span = max - min || 1;
  const y = (v) => CHART_HEIGHT - ((v - min) / span) * CHART_HEIGHT;
  const x = (i) => (i / (totals.length - 1)) * CHART_WIDTH;

  return {
    points: totals.map((v, i) => `${x(i).toFixed(1)},${y(v).toFixed(1)}`).join(' '),
    zeroY: y(0),
    events: totals.length,
    final: totals[totals.length - 1],
  };
}

// The Growth playbook's real record, shown beside the home hero.
// Renders nothing if the playbook can't be read.
export default function HeroStatement() {
  const [playbook, setPlaybook] = useState(null);
  const [status, setStatus] = useState('loading');

  useEffect(() => {
    let cancelled = false;
    getDoc(doc(db, 'playbooks', PLAYBOOK_ID))
      .then((snapshot) => {
        if (cancelled) return;
        if (snapshot.exists()) {
          setPlaybook(snapshot.data());
          setStatus('ready');
        } else {
          setStatus('missing');
        }
      })
      .catch(() => {
        if (!cancelled) setStatus('missing');
      });
    return () => {
      cancelled = true;
    };
  }, []);

  if (status === 'loading') {
    return <div className="statement statement-placeholder" aria-hidden="true" />;
  }
  if (status !== 'ready') return null;

  const avgReturn = playbook.avg_returns ?? playbook.distributions_summary?.returns_mean;
  const avgAccuracy = playbook.avg_accuracy ?? playbook.distributions_summary?.accuracy_mean;
  const streak = playbook.longest_win_streak;
  const chart = Array.isArray(playbook.returns_by_event) ? cumulativePath(playbook.returns_by_event) : null;

  const figures = [];
  if (returnsToCentered(avgReturn) !== null) {
    figures.push({
      value: formatReturnsCentered(avgReturn),
      label: 'Average return',
      tone: returnsToCentered(avgReturn) >= 0 ? 'positive' : 'negative',
    });
  }
  if (typeof avgAccuracy === 'number') {
    figures.push({ value: `${(avgAccuracy * 100).toFixed(1)}%`, label: 'Average accuracy' });
  }
  if (typeof streak === 'number') {
    figures.push({ value: `${streak}`, label: 'Longest win streak (events)' });
  }

  if (figures.length === 0 && !chart) return null;

  return (
    <aside className="statement" aria-label="Growth playbook record">
      <div className="statement-head">
        <div>
          <h2>Growth playbook</h2>
          <p>Our low-risk playbook, event by event.</p>
        </div>
        <span className="tag tag-purple">Odikai</span>
      </div>

      {figures.length > 0 && (
        <div className="statement-figures">
          {figures.map((f) => (
            <div className="figure" key={f.label}>
              <span className={`figure-value ${f.tone || ''}`}>{f.value}</span>
              <span className="figure-label">{f.label}</span>
            </div>
          ))}
        </div>
      )}

      {chart && (
        <div className="statement-chart">
          <svg viewBox={`0 0 ${CHART_WIDTH} ${CHART_HEIGHT}`} preserveAspectRatio="none" role="img"
            aria-label={`Running total over ${chart.events} events`}>
            <line x1="0" x2={CHART_WIDTH} y1={chart.zeroY} y2={chart.zeroY}
              stroke="#3A3349" strokeWidth="1" strokeDasharray="4 4" vectorEffect="non-scaling-stroke" />
            <polyline points={chart.points} fill="none" stroke="#9B72E3" strokeWidth="2"
              strokeLinejoin="round" strokeLinecap="round" vectorEffect="non-scaling-stroke" />
          </svg>
          <div className="statement-chart-legend">
            <span>Running total, {chart.events} events</span>
            <span>Dashed line: break-even</span>
          </div>
        </div>
      )}

      <div className="statement-foot">
        <span>Past results don't guarantee future returns.</span>
        <Link to={`/playbooks/${PLAYBOOK_ID}`}>See the playbook</Link>
      </div>
    </aside>
  );
}
